// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useMemo } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { pollStrings } from './strings';
import type { Poll, PollOptionId, PollVoter } from './types';
import { winningOptionIds } from './usePollComposerViewModel';

export interface PollResultsSheetProps {
  readonly visible: boolean;
  readonly poll: Poll;
  readonly voters: readonly PollVoter[];
  readonly onClose: () => void;
}

/**
 * Modal sheet listing the per-option vote breakdown. Read-only. For
 * `closed`/`tied` polls renders trophy glyphs against winners. For
 * `anonymous` polls hides voter names + shows a notice instead.
 *
 * Voter rows render names only. Voter `userId` is never rendered (cross-
 * platform contract: short stable display only).
 */
export const PollResultsSheet: React.FC<PollResultsSheetProps> = ({
  visible,
  poll,
  voters,
  onClose,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const winners = useMemo(() => {
    if (poll.state === 'open') {
      return new Set<string>();
    }
    return new Set(winningOptionIds(poll.options));
  }, [poll]);

  const votersByOption = useMemo(() => {
    const map = new Map<PollOptionId, PollVoter[]>();
    for (const opt of poll.options) {
      map.set(opt.id, []);
    }
    for (const v of voters) {
      const list = map.get(v.optionId);
      if (list !== undefined) {
        list.push(v);
      }
    }
    return map;
  }, [poll.options, voters]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  return (
    <Modal visible={visible} onClose={handleClose} accessibilityLabel={pollStrings.resultsTitle}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text.primary }]}>
            {pollStrings.resultsTitle}
          </Text>
          <Pressable
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel={pollStrings.resultsClose}
            style={({ pressed }) => [styles.closeButton, pressed && { opacity: 0.6 }]}
          >
            <Text style={[styles.closeText, { color: theme.colors.text.secondary }]}>×</Text>
          </Pressable>
        </View>
        <Text style={[styles.question, { color: theme.colors.text.primary }]} numberOfLines={4}>
          {poll.question}
        </Text>
        {poll.anonymous && (
          <Text style={[styles.notice, { color: theme.colors.text.tertiary }]}>
            {pollStrings.resultsAnonymousNotice}
          </Text>
        )}
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {poll.options.map((opt) => {
            const optionVoters = votersByOption.get(opt.id) ?? [];
            const isWinner = winners.has(opt.id);
            return (
              <View
                key={opt.id}
                style={[styles.optionGroup, { borderColor: theme.colors.border.secondary }]}
              >
                <View style={styles.optionHeader}>
                  {isWinner && (
                    <Text style={styles.trophy} accessibilityElementsHidden>
                      ★
                    </Text>
                  )}
                  <Text style={[styles.optionText, { color: theme.colors.text.primary }]}>
                    {opt.text}
                  </Text>
                  <Text style={[styles.optionCount, { color: theme.colors.text.secondary }]}>
                    {opt.voteCount}
                  </Text>
                </View>
                {!poll.anonymous && optionVoters.length === 0 && (
                  <Text style={[styles.noVoters, { color: theme.colors.text.tertiary }]}>
                    {pollStrings.resultsNoVotersYet}
                  </Text>
                )}
                {!poll.anonymous &&
                  optionVoters.map((v) => (
                    <Text
                      key={`${v.optionId}-${v.userId}`}
                      style={[styles.voter, { color: theme.colors.text.secondary }]}
                      numberOfLines={1}
                    >
                      {v.displayName}
                    </Text>
                  ))}
              </View>
            );
          })}
        </ScrollView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxHeight: 540,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  } satisfies ViewStyle,
  title: {
    fontSize: 17,
    fontWeight: '700',
  } satisfies TextStyle,
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  closeText: {
    fontSize: 22,
    lineHeight: 22,
  } satisfies TextStyle,
  question: {
    fontSize: 15,
    fontWeight: '500',
    marginBottom: 8,
  } satisfies TextStyle,
  notice: {
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 8,
  } satisfies TextStyle,
  scroll: {
    flexGrow: 0,
  } satisfies ViewStyle,
  scrollContent: {
    paddingBottom: 8,
  } satisfies ViewStyle,
  optionGroup: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingVertical: 8,
  } satisfies ViewStyle,
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  } satisfies ViewStyle,
  trophy: {
    marginRight: 6,
    fontSize: 14,
  } satisfies TextStyle,
  optionText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  optionCount: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
  voter: {
    fontSize: 13,
    paddingVertical: 2,
    paddingLeft: 16,
  } satisfies TextStyle,
  noVoters: {
    fontSize: 12,
    paddingLeft: 16,
    fontStyle: 'italic',
  } satisfies TextStyle,
});
